import { type FC, type PropsWithChildren, useMemo } from 'react'
import classNames from 'classnames'
import styles from './MeetingInsights.module.scss'
import { MEETING_INSIGHT_THRESHOLDS } from './meetingInsightsConfig'
import { getVisibleMeetingInsightRows, type VisibleMeetingInsightRows } from './getVisibleMeetingInsightRows'
import { MeetingInsightsCard } from './MeetingInsightsCard'
import { ADVISOR_INSIGHTS_TIME_RANGE, type InsightsDashboardData, TIME_RANGE_LABELS } from '../../../../../../shared/insights'

interface InsightsRowProps extends PropsWithChildren {
  icon: string
}

const InsightsRow: FC<InsightsRowProps> = ({ icon, children }) => {
  return (
    <div className='d-flex align-items-center zocks-gap-0.75'>
      <div
        className={classNames(
          'd-flex align-items-center justify-content-center flex-shrink-0 zocks-bg-color-gray-100 border zocks-border-color-gray-200 zocks-border-0.5 zocks-text-primary',
          styles.iconBox,
        )}
      >
        <i className={`bi bi-${icon} fs-6`} aria-hidden />
      </div>
      <div className='flex-1 mw-0 zocks-text-gray-900'>{children}</div>
    </div>
  )
}

type MeetingInsightsContentProps = VisibleMeetingInsightRows

const MeetingInsightsContent: FC<MeetingInsightsContentProps> = ({ topTopics, topOptimisticAssetClass, heldAway, referralAsk }) => {
  return (
    <>
      {topTopics.length > 0 && (
        <InsightsRow icon='chat-dots'>
          {topTopics.length === 1 ? (
            <>
              Expect <strong>{topTopics[0]}</strong> to come up
            </>
          ) : (
            <>
              Expect <strong>{topTopics[0]}</strong> and <strong>{topTopics[1]}</strong> to come up
            </>
          )}
        </InsightsRow>
      )}
      {topOptimisticAssetClass && (
        <InsightsRow icon='binoculars'>
          <>
            Clients are most optimistic about <strong>{topOptimisticAssetClass}</strong>
          </>
        </InsightsRow>
      )}
      {heldAway && (
        <InsightsRow icon='bullseye'>
          <>
            Good moment to <strong>probe held-away assets</strong>: {heldAway.agreedPercent}% turn into agreements
          </>
        </InsightsRow>
      )}
      {referralAsk && (
        <InsightsRow icon='people'>
          <>
            Consider <strong>making a referral ask:</strong> {referralAsk.conversionRate}% succeed in these meetings
          </>
        </InsightsRow>
      )}
    </>
  )
}

interface MeetingInsightsProps {
  data: InsightsDashboardData
}

export const MeetingInsights: FC<MeetingInsightsProps> = ({ data }) => {
  const { topTopics, topOptimisticAssetClass, heldAway, referralAsk } = useMemo(
    () => getVisibleMeetingInsightRows({ data, config: MEETING_INSIGHT_THRESHOLDS }),
    [data],
  )

  const hasContent = data.highlights.meetingCount !== 0 && (topTopics.length > 0 || topOptimisticAssetClass || heldAway || referralAsk)
  const hasReliableMeetingCount = data.highlights.meetingCount >= MEETING_INSIGHT_THRESHOLDS.minReliableMeetingCount
  const infoBoxTitle =
    hasContent && !hasReliableMeetingCount ? 'Limited data: This meeting type has few results in the last 30 days. Insights may be less reliable.' : undefined

  return (
    <MeetingInsightsCard
      title='Insights for this meeting'
      subtitle={`Based on the last ${TIME_RANGE_LABELS[ADVISOR_INSIGHTS_TIME_RANGE]}`}
      infoBoxTitle={infoBoxTitle}
    >
      {hasContent && (
        <MeetingInsightsContent topTopics={topTopics} topOptimisticAssetClass={topOptimisticAssetClass} heldAway={heldAway} referralAsk={referralAsk} />
      )}
      {!hasContent && <div className='zocks-text-gray-500'>No insights yet. Zocks needs more data to find patterns.</div>}
    </MeetingInsightsCard>
  )
}
